import { memo, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Icon } from "@iconify/react";
import { isDesktop } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarkitHeaderLogo from "../../components/MarkitHeaderLogo";
import RectangleButton from "../../components/Buttons/RectangleButton";
import {
  LandingPageNavigationId,
  NavigationId,
} from "../../navigation/AppParamList";
import { Colors } from "../../utils/colors";
import { MARKITAI } from "@markit/common.utils";

// The `result` the backend redirects back with after the Google consent flow
type ConnectionResult = "success" | "denied" | "already_connected" | "error";

const CONNECTION_CONTENT: Record<
  ConnectionResult,
  { header: string; title: string; subtitle: string; buttonLabel: string }
> = {
  success: {
    header: "Success!",
    title: "Google Calendar is connected!",
    subtitle:
      "Turn on Google Calendar Invites when you create an event to add it to your calendar and invite guests when they RSVP.",
    buttonLabel: "Let's Start",
  },
  denied: {
    header: "Not Connected",
    title: "Google Calendar Wasn't Connected",
    subtitle:
      "Markit needs permission to manage events on your calendar. Please try again from your integrations page and make sure the calendar permission is checked.",
    buttonLabel: "Back to Markit",
  },
  already_connected: {
    header: "Something Failed!",
    title: "Google Account Already Connected",
    subtitle:
      "It's linked to another Markit account. Please connect a different Google account, or disconnect it from the other Markit account first.",
    buttonLabel: "Back to Markit",
  },
  error: {
    header: "Something Failed!",
    title: "Google Calendar Connection Failed",
    subtitle:
      "We could not connect your Google Calendar. Please try again from your integrations page.",
    buttonLabel: "Back to Markit",
  },
};

const isConnectionResult = (value: string | null): value is ConnectionResult =>
  value !== null && value in CONNECTION_CONTENT;

const GoogleCalendarConnectionPage = memo(
  function GoogleCalendarConnectionPageFn() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const result = searchParams.get("result");

    // Visiting the page directly, without a result, has nothing to show
    useEffect(() => {
      if (!isConnectionResult(result)) {
        navigate(NavigationId.HOME_INTEGRATIONS, { replace: true });
      }
    }, [navigate, result]);

    if (!isConnectionResult(result)) {
      return null;
    }

    const content = CONNECTION_CONTENT[result];
    const isSuccess = result === "success";

    return (
      <div
        style={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Helmet>
          <title>Google Calendar Connection | Markit AI</title>
          <meta
            name="description"
            content={
              isSuccess
                ? "Your Google Calendar has been successfully connected."
                : "Your Google Calendar could not be connected."
            }
          />
          <link
            rel="canonical"
            href={MARKITAI + LandingPageNavigationId.GOOGLE_CALENDAR_CONNECTION}
          />
        </Helmet>
        <div style={{ position: "absolute", top: 24, left: 24 }}>
          <MarkitHeaderLogo fullLogo alternateSize={24} />
        </div>
        <div
          className="ColumnCenter"
          style={{
            minHeight: "100vh",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className={isDesktop ? "AlignedRow" : "ColumnNormal"}
            style={{
              width: "100%",
              maxWidth: isDesktop ? 920 : 480,
              gap: isDesktop ? 80 : 14,
              alignItems: isDesktop ? "center" : "flex-start",
              justifyContent: "center",
            }}
          >
            {isDesktop ? null : (
              <div style={{ paddingInline: 14 }}>
                <Icon icon="logos:google-calendar" height={40} />
              </div>
            )}
            <div
              className="ColumnNormal"
              style={{
                maxWidth: 480,
                gap: isDesktop ? 24 : 14,
                paddingInline: isDesktop ? 0 : 14,
              }}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span
                  className="bodyMedium"
                  style={{ color: isSuccess ? Colors.BLUE5 : Colors.RED3 }}
                >
                  {content.header}
                </span>
                <span
                  style={{
                    fontSize: 34,
                    fontWeight: 500,
                    display: "block",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {content.title}
                </span>
                <span className="bodySubtext" style={{ maxWidth: 400 }}>
                  {content.subtitle}
                </span>
              </div>
              <RectangleButton
                buttonLabel={content.buttonLabel}
                onPress={() => navigate(NavigationId.HOME_INTEGRATIONS)}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={40}
                altPaddingVert={14}
                containerStyles={{ width: "fit-content" }}
              />
            </div>
            {isDesktop ? (
              <Icon icon="logos:google-calendar" height={150} />
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default GoogleCalendarConnectionPage;
